import '../../motion.css';
import './build.css';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import { buildExperienceCopy } from '@/i18n/ns/build-experience';
import { STAGGER_FACE_MS, stagger, useEnterOnce, useFlip, useReducedMotion } from '@/lib/motion';

/** How long "Publishing…" holds at least, so the moment reads as a breath, not a flicker. */
export const PUBLISH_BREATH_MS = 700;

/**
 * The status to show: once `publishing` has been seen, it stays for at least `ms` before
 * `done` replaces it. Done that arrives first (nothing to wait for) shows at once.
 */
export function useBreath(status: 'publishing' | 'done', ms = PUBLISH_BREATH_MS): 'publishing' | 'done' {
  const since = useRef<number | null>(null);
  const [shown, setShown] = useState(status);
  useEffect(() => {
    if (status === 'publishing') {
      since.current ??= Date.now();
      setShown('publishing');
      return;
    }
    const started = since.current;
    if (started === null) return setShown('done');
    const wait = Math.max(0, started + ms - Date.now());
    const timer = window.setTimeout(() => {
      since.current = null;
      setShown('done');
    }, wait);
    return () => window.clearTimeout(timer);
  }, [status, ms]);
  return shown;
}

export type MomentPerson = { id: string; initials: string; name: string };

/**
 * The publish moment (NOTES-v3 moment e) for a person's own Publish. "Publishing…" holds for a
 * breath; then the same sheet becomes the confirmation: a circle and check draw themselves with
 * one soft ring, the title says who it is live for, the people appear face by face, then what
 * they'll see and where, then the actions. It plays once per `momentKey` (the published
 * version); a revisit shows it still. All words come from the caller's VM, not from here.
 * An agent's direct publish never uses this: its line says "Published by <agent>" quietly.
 */
export function PublishMoment({
  momentKey,
  status,
  title,
  sub,
  people = [],
  peopleCaption,
  note,
  actions,
}: {
  momentKey: string;
  status: 'publishing' | 'done';
  title: ReactNode;
  sub?: ReactNode;
  people?: readonly MomentPerson[];
  peopleCaption?: ReactNode;
  note?: ReactNode;
  actions?: ReactNode;
}) {
  const copy = useNamespaceCopy(buildExperienceCopy);
  const shown = useBreath(status);
  const first = useEnterOnce(`publish:${momentKey}:${shown}`);
  const reduce = useReducedMotion();
  const play = first && !reduce && shown === 'done';
  // Motion joins the element's own class; it never replaces it.
  const at = (own: string, ms: number, motion = 'pc-m-reveal') =>
    play ? { className: `${own} ${motion}`, style: stagger(0, { base: ms }) } : { className: own };
  if (shown === 'publishing') {
    return (
      <div className="pc-publish" data-state="publishing" data-testid="publish-moment" role="status">
        <span className="pc-build-dot pc-m-breathe" aria-hidden="true" />
        <span>{copy.publishing}</span>
      </div>
    );
  }
  return (
    <div className="pc-publish" data-state="done" data-testid="publish-moment" data-playing={play || undefined}>
      <span className="pc-publish-mark" aria-hidden="true">
        {play && <span className="pc-publish-ring pc-m-ring" style={stagger(0, { base: 620 })} />}
        <svg viewBox="0 0 56 56" width="56" height="56" className={play ? 'pc-m-checkdraw' : undefined}>
          <circle cx="28" cy="28" r="25" pathLength={1} className="pc-publish-circle" style={play ? stagger(0, { base: 60 }) : undefined} />
          <path d="M18 29l7 7 13-15" pathLength={1} className="pc-publish-check" style={play ? stagger(0, { base: 420 }) : undefined} />
        </svg>
      </span>
      <h2 role="status" {...at('pc-publish-title', 160)}>
        {title}
      </h2>
      {sub && (
        <p {...at('pc-publish-sub', 240)}>
          {sub}
        </p>
      )}
      {people.length > 0 && (
        <ul className="pc-publish-people" aria-label={typeof peopleCaption === 'string' ? peopleCaption : undefined}>
          {people.map((p, i) => (
            <li
              key={p.id}
              className={play ? 'pc-publish-face pc-m-pop' : 'pc-publish-face'}
              style={play ? stagger(i, { base: 400, step: STAGGER_FACE_MS }) : undefined}
              title={p.name}
            >
              <span aria-hidden="true">{p.initials}</span>
              <span className="sr-only">{p.name}</span>
            </li>
          ))}
        </ul>
      )}
      {peopleCaption && (
        <p {...at('pc-publish-caption', 760, 'pc-m-fade')}>
          {peopleCaption}
        </p>
      )}
      {note && (
        <p {...at('pc-publish-note', 760, 'pc-m-fade')}>
          {note}
        </p>
      )}
      {actions && (
        <div {...at('pc-publish-actions', 560, 'pc-m-fade')}>
          {actions}
        </div>
      )}
    </div>
  );
}

/**
 * "This is what your answer changed" (NOTES-v3 moment d): one soft halo on the thing an
 * answer changed. It plays when `changeKey` changes after mount (the person answered), never on
 * the first render and never on everyday re-renders. The halo restarts on the same element
 * (class off, reflow, class on): the children keep their identity and their state.
 */
export function Reshape({ changeKey, children, className }: { changeKey: string; children: ReactNode; className?: string }) {
  const element = useRef<HTMLDivElement>(null);
  const seen = useRef(changeKey);
  const [pulses, setPulses] = useState(0);
  useLayoutEffect(() => {
    if (changeKey === seen.current) return;
    seen.current = changeKey;
    const el = element.current;
    if (el) {
      el.classList.remove('pc-m-halo');
      void el.offsetWidth; // restart the one-shot animation without remounting anything
      el.classList.add('pc-m-halo');
    }
    setPulses((n) => n + 1);
  }, [changeKey]);
  return (
    <div ref={element} className={className} data-reshape={pulses || undefined}>
      {children}
    </div>
  );
}

/**
 * A list whose order an answer changes ("What opens first"): kept items slide to their new
 * place (FLIP, transform only); items the answer added fade in only after the move, where the
 * space opened. Children carry `data-flip-key` via `itemKey`.
 */
export function ReshapeList<T>({
  items,
  itemKey,
  render,
  orderKey,
  className,
}: {
  items: readonly T[];
  itemKey: (item: T) => string;
  render: (item: T) => ReactNode;
  /** Changes when the order or membership changes. */
  orderKey: string;
  className?: string;
}) {
  const container = useRef<HTMLDivElement>(null);
  const seen = useRef<Set<string> | null>(null);
  useFlip(container, [orderKey]);
  const initial = seen.current === null;
  const known = seen.current ?? new Set<string>();
  useEffect(() => {
    seen.current = new Set(items.map(itemKey));
  });
  return (
    <div ref={container} className={className}>
      {items.map((item) => {
        const key = itemKey(item);
        const added = !initial && !known.has(key);
        return (
          <div
            key={key}
            data-flip-key={key}
            className={added ? 'pc-m-fade' : undefined}
            // added items wait for the move (moment d: "fades in only after the columns moved")
            style={added ? stagger(0, { base: 300 }) : undefined}
          >
            {render(item)}
          </div>
        );
      })}
    </div>
  );
}
